import React, { useLayoutEffect, useRef } from "react";
import { getYear } from "date-fns";
import type { DateRange } from "@daypicker/react";
import { useConstant } from "@hooks/useConstant";
import type { DatePickerSelectionMode } from "./DatePicker.types";
import {
    areRangesEqual,
    getMonthRange,
    getQuarterRange,
    getYearRange,
    MONTH_LABELS,
    parseDateOnly,
} from "./DatePicker.lib";
import styles from "./DatePicker.module.scss";

const DEFAULT_PAST_YEAR_COUNT = 20;
const DEFAULT_FUTURE_YEAR_COUNT = 6;

const scrollNodeToContainerStart = (
    container: HTMLElement,
    node: HTMLElement,
) => {
    const containerRect = container.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    container.scrollTop = Math.max(
        0,
        nodeRect.top - containerRect.top + container.scrollTop,
    );
};

interface DatePickerPeriodCalendarProps {
    mode: Extract<DatePickerSelectionMode, "month" | "quarter" | "year">;
    value?: DateRange;
    onValueChange: (value: DateRange | undefined) => void;
    required?: boolean;
    minDate?: Date;
    maxDate?: Date;
    className?: string;
}

interface Period {
    label: string;
    range: DateRange;
}

const isPeriodDisabled = ({
    range,
    minDate,
    maxDate,
}: {
    range: DateRange;
    minDate?: Date;
    maxDate?: Date;
}) =>
    Boolean(
        !range.from ||
        !range.to ||
        (minDate && range.to < minDate) ||
        (maxDate && range.from > maxDate),
    );

const getVisibleYears = ({
    selectedDate,
    minDate,
    maxDate,
}: {
    selectedDate?: Date;
    minDate?: Date;
    maxDate?: Date;
}) => {
    const currentYear = getYear(new Date());
    const selectedYear = selectedDate ? getYear(selectedDate) : currentYear;
    const firstYear = minDate
        ? getYear(minDate)
        : Math.min(currentYear - DEFAULT_PAST_YEAR_COUNT, selectedYear);
    const lastYear = maxDate
        ? getYear(maxDate)
        : Math.max(currentYear + DEFAULT_FUTURE_YEAR_COUNT, selectedYear + 2);

    return Array.from(
        { length: Math.max(lastYear - firstYear + 1, 0) },
        (_, index) => firstYear + index,
    );
};

const getPeriodStartDate = (year: number, monthIndex: number): Date => {
    const month = String(monthIndex + 1).padStart(2, "0");
    const date = parseDateOnly(`${year}-${month}-01`);
    if (!date) {
        throw new Error(`Invalid period start date: ${year}-${month}-01`);
    }
    return date;
};

const getPeriods = (
    mode: DatePickerPeriodCalendarProps["mode"],
    year: number,
): Period[] => {
    if (mode === "month") {
        return MONTH_LABELS.map((label, monthIndex) => ({
            label,
            range: getMonthRange(getPeriodStartDate(year, monthIndex)),
        }));
    }
    if (mode === "quarter") {
        return Array.from({ length: 4 }, (_, quarterIndex) => ({
            label: `Q${quarterIndex + 1}`,
            range: getQuarterRange(getPeriodStartDate(year, quarterIndex * 3)),
        }));
    }
    return [
        {
            label: String(year),
            range: getYearRange(getPeriodStartDate(year, 0)),
        },
    ];
};

export const DatePickerPeriodCalendar = ({
    mode,
    value,
    onValueChange,
    required = false,
    minDate,
    maxDate,
    className,
}: DatePickerPeriodCalendarProps) => {
    const years = getVisibleYears({
        selectedDate: value?.from,
        minDate,
        maxDate,
    });
    const currentYear = useConstant(() => getYear(new Date()));
    const focusedYear = value?.from ? getYear(value.from) : currentYear;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const focusedYearRef = useRef<HTMLElement | null>(null);
    const setFocusedYearNode = (node: HTMLElement | null) => {
        focusedYearRef.current = node;
    };

    useLayoutEffect(() => {
        const container = containerRef.current;
        const focusedNode = focusedYearRef.current;
        if (!container || !focusedNode) {
            return;
        }

        let secondFrameId = 0;
        // Popover positioning can settle after the first layout pass; wait
        // until the scroll container has its final max-height before scrolling.
        const firstFrameId = requestAnimationFrame(() => {
            scrollNodeToContainerStart(container, focusedNode);
            secondFrameId = requestAnimationFrame(() => {
                scrollNodeToContainerStart(container, focusedNode);
            });
        });

        return () => {
            cancelAnimationFrame(firstFrameId);
            cancelAnimationFrame(secondFrameId);
        };
    }, [focusedYear, mode]);

    return (
        <div className={className}>
            <div ref={containerRef} className={styles.periodSelectorContent}>
                {years.map((year) => {
                    const periods = getPeriods(mode, year);
                    const focusedYearRefProp =
                        year === focusedYear ? setFocusedYearNode : undefined;

                    if (mode === "year") {
                        const period = periods[0];
                        if (!period) {
                            return null;
                        }
                        const isSelected = areRangesEqual(value, period.range);
                        return (
                            <button
                                key={year}
                                type="button"
                                ref={focusedYearRefProp}
                                className={styles.periodYearButton}
                                data-selected={isSelected || undefined}
                                disabled={isPeriodDisabled({
                                    range: period.range,
                                    minDate,
                                    maxDate,
                                })}
                                onClick={() =>
                                    onValueChange(
                                        isSelected && !required
                                            ? undefined
                                            : period.range,
                                    )
                                }
                            >
                                {period.label}
                            </button>
                        );
                    }

                    return (
                        <section
                            key={year}
                            ref={focusedYearRefProp}
                            className={styles.periodYearGroup}
                        >
                            <div className={styles.periodYearLabel}>{year}</div>
                            <div
                                className={
                                    mode === "month"
                                        ? styles.periodMonthGrid
                                        : styles.periodQuarterGrid
                                }
                            >
                                {periods.map((period) => {
                                    const isSelected = areRangesEqual(
                                        value,
                                        period.range,
                                    );
                                    return (
                                        <button
                                            key={period.label}
                                            type="button"
                                            className={styles.periodCell}
                                            data-selected={
                                                isSelected || undefined
                                            }
                                            disabled={isPeriodDisabled({
                                                range: period.range,
                                                minDate,
                                                maxDate,
                                            })}
                                            onClick={() =>
                                                onValueChange(
                                                    isSelected && !required
                                                        ? undefined
                                                        : period.range,
                                                )
                                            }
                                        >
                                            {period.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </section>
                    );
                })}
            </div>
        </div>
    );
};
